import { v4 as uuidv4 } from 'uuid';
import { findFieldBy } from '@icp/form-schema';
import { isMobile, parseJSON } from '@icp/utils';
import { transformToTableElement, makeTableElement } from './IcpTable/utils';
import { transformToEChartElement } from './IcpChart/utils';
import { transformToGenericEChartElement } from './IcpGenericChart/utils';
import restApi from '../services/restApi';

export function makeTitleElement(title) {
  return {
    component: 'Text',
    componentProps: {
      content: title,
      style: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
  };
}

const GRID_ID = 'grid';

function makeGridElement(fields) {
  return {
    id: GRID_ID,
    component: 'Grid',
    componentProps: {
      colNumber: 3,
    },
    fields,
  };
}

function findCodeBlock(str) {
  const results = [];
  const regex = /```(icp-table|icp-chart|icp-generic-chart)([\s\S]*?)```/g;
  for (let match = regex.exec(str); match !== null; match = regex.exec(str)) {
    results.push([match[1], match[2]]);
  }
  return results;
}

let taskChain = Promise.resolve();

export async function handleSave(answerMessageContent, formApi, prompt) {
  if (!answerMessageContent) return;

  const elements = await Promise.all(
    findCodeBlock(answerMessageContent).map(([type, content]) => {
      return {
        'icp-table': transformToTableElement,
        'icp-chart': transformToEChartElement,
        'icp-generic-chart': transformToGenericEChartElement,
      }[type]?.(content);
    }),
  ).then((list) =>
    list
      .filter(Boolean)
      .map(([element, title, formEntity]) => wrapElement(element, title, formEntity, prompt)),
  );

  if (elements.length === 0) return;

  const task = async () => {
    const currentDsh = await restApi.get('/form/api/dashboard/personal');
    let schema = parseJSON(currentDsh.schemaJson) || {};
    const foundGrid = findFieldBy(schema, (field) => field.id === GRID_ID)?.field;
    if (foundGrid) {
      foundGrid.fields = [...(foundGrid.fields || []), ...elements];
    } else {
      schema = {
        ...schema,
        fields: [makeGridElement([...(schema.fields || []), ...elements])],
      };
    }
    await restApi.put('/form/api/dashboard/personal', {
      ...currentDsh,
      schemaJson: JSON.stringify(schema),
    });
    if (window.location.pathname.includes('personal-dashboard')) {
      formApi?.changeSchema(() => schema);
    }
  };

  taskChain = taskChain.finally(() => task());
}

export function wrapElement(element, title, formEntity /* , prompt */) {
  const portletId = `portlet-${uuidv4()}`;

  return {
    id: portletId,
    span: isMobile() ? 3 : 1,
    component: 'Card',
    componentProps: {
      'data-portlet-id': portletId,
    },
    fields: [
      {
        component: 'Stack',
        componentProps: {
          flexDirection: 'column',
        },
        fields: [
          {
            component: 'Stack',
            componentProps: {
              flexDirection: 'row',
            },
            fields: [
              makeTitleElement(title),
              {
                component: 'Box',
                style: {
                  flex: 1,
                },
              },
              {
                component: 'Button',
                componentProps: {
                  icon: 'oct:arrow-both',
                  type: 'text',
                  action: {
                    type: 'formMethod',
                    methodName: 'toggleWidth',
                  },
                },
              },
              {
                component: 'Button',
                componentProps: {
                  icon: 'material:arrow-back',
                  type: 'text',
                  action: {
                    type: 'formMethod',
                    methodName: 'portletMoveBack',
                  },
                },
              },
              {
                component: 'Button',
                componentProps: {
                  icon: 'material:arrow-forward',
                  type: 'text',
                  action: {
                    type: 'formMethod',
                    methodName: 'portletMoveForward',
                  },
                },
              },
              {
                component: 'Button',
                componentProps: {
                  icon: 'material:clear-rounded',
                  type: 'text',
                  action: {
                    type: 'confirm',
                    content: 'Are you sure you want to delete this portlet?',
                    successAction: {
                      type: 'request',
                      method: 'put',
                      url: '/form/api/dashboard/personal',
                      transformDataRequest: `function findById(fields, targetId) { if (!(fields !== null && fields !== void 0 && fields.length)) return; var idx = fields.findIndex(function (f) { return f.id === targetId; }); if (idx >= 0) { fields.splice(idx, 1); return; } fields.forEach(function (f) { return findById(f.fields, targetId); });}(function transform() { findById(formSchema.fields, '${portletId}'); return { id: context.dashboardId, schemaJson: JSON.stringify(formSchema) };})(this);`,
                      successAction: {
                        type: 'refresh',
                      },
                    },
                  },
                },
              },
            ],
          },
          // {
          //   component: 'Text',
          //   componentProps: {
          //     content: prompt || '',
          //     style: {
          //       color: 'var(--helper-color, #979797)',
          //     },
          //   },
          // },
          element,
          !!formEntity && {
            component: 'Button',
            style: {
              alignSelf: 'start',
            },
            componentProps: {
              content: `View Report (${formEntity.name})`,
              type: 'link',
              action: {
                type: 'dialog',
                openDialogProps: {
                  title: `View Report (${formEntity.name})`,
                  hideFooter: true,
                  centered: true,
                  width: '90vw',
                },
                openFormProps: {
                  FormRendererProps: {
                    schema: {
                      fields: [makeTableElement(formEntity)],
                    },
                  },
                },
              },
            },
          },
        ].filter(Boolean),
      },
    ],
  };
}
